// useRepeatLastDose — shared hook for the "long-press FAB to repeat
// the last dose" affordance. Reads qk.lastDose.v2 from localStorage,
// posts a clean duplicate, and surfaces a toast + haptic. Returns a
// `tryRepeat` function and an `available` flag — caller decides
// whether to wire it into a tap-or-hold gesture.
//
// Available iff:
//   - lastDose exists in localStorage (per-device), and
//   - its ts is <12h old (older = probably a different pattern day,
//     so a silent repeat would be misleading)

import { useCallback } from 'react';
import { apiPost } from './api';
import { apiErrorMessage, isQueuedOffline } from './apiErrors';
import { haptics } from './haptics';
import { useToast } from '../components/ui/Toast';
import { readLastDose, writeLastDose } from '../screens/log/LogDoseSheet';

export function useRepeatLastDose() {
  const toast = useToast();
  const last = readLastDose();
  const recent = !!last && !!last.ts && Date.now() - new Date(last.ts).getTime() < 12 * 3600_000;

  const tryRepeat = useCallback(async (onSaved?: () => void): Promise<boolean> => {
    const prior = readLastDose();
    if (!prior || !prior.ts) return false;
    const age = Date.now() - new Date(prior.ts).getTime();
    if (age >= 12 * 3600_000) return false;
    const u: 'mg' | 'g' | 'mL' = prior.form === 'tablet_7oh' ? 'mg' : prior.form === 'leaf' ? 'g' : 'mL';
    try {
      await apiPost('/api/doses', {
        form: prior.form,
        amount: prior.amount,
        amountUnit: u,
        concentrationMgPerMl: prior.form === 'extract' ? prior.concentration : undefined,
        strain: prior.form === 'leaf' && prior.strain ? prior.strain : undefined,
      });
      writeLastDose({ ...prior, ts: new Date().toISOString() });
      haptics.confirm();
      const unitLabel = u;
      toast.push(`Same as last (${prior.amount} ${unitLabel}) — saved`);
      onSaved?.();
      return true;
    } catch (err) {
      if (!isQueuedOffline(err)) {
        toast.push(apiErrorMessage(err, 'Could not log — try again.'), { tone: 'error' });
      }
      // Even on failure we say "handled" (true) so the caller doesn't
      // also open the sheet — the user has already seen the toast.
      return true;
    }
  }, [toast]);

  return { tryRepeat, available: recent };
}
